import React, {useState, useEffect, useContext, createContext} from 'react';
import Socket from '../../Services/Socket';
import { usePreference } from '../../Data/Preference';
import { ulid } from 'ulid';
import API from '../../Services/API';
import { hashCode, useVisibilityChange } from '../../Services/helpers';
import { useSession } from '../Session';

const SocketContext = createContext();

export const useSocketContext = () => {
    return useContext(SocketContext);
};

let reconnectInterval;
const subscriptions = {};
const subscriptionQueue = [];
const subscriptionTimeout = 50;

const Provider = ({children}) => {

    const {hasToken} = useSession();
    const [channelId] = usePreference('socket-channel', ulid(), true);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        if (!hasToken) {
            return;
        }

        Socket.connect();

        const onOpenUnsubscribe = Socket.onOpen(() => {
            // console.log('Socket connected!!');
            resubscribe();
            setIsConnected(true);
        });
        const onCloseUnsubscribe = Socket.onClose(() => {
            // console.log('Socket closed. Reconnecting in 0.25 second');
            setTimeout(() => Socket.connect(), 250);
            setIsConnected(false);
        });
        const onErrorUnsubscribe = Socket.onError(() => {
            // console.log('Socket errored. Reconnecting in 0.25 second');
            setTimeout(() => Socket.connect(), 250);
            setIsConnected(false);
        });

        const onMessageUnsubscribe = Socket.onMessage(handleEvent);

        reconnectInterval = setInterval(() => {
            Socket.connect();
        }, 1000 * 60);

        return () => {
            onOpenUnsubscribe();
            onCloseUnsubscribe();
            onErrorUnsubscribe();
            onMessageUnsubscribe();
            clearInterval(reconnectInterval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId, hasToken]);

    useVisibilityChange(() => {
        if (!hasToken) {
            return;
        }

        Socket.connect();
    });


    const handleEvent = (event) => {
        Object.values(subscriptions).forEach(sub => {
            const {subscription, callback} = sub;

            if (subscription?.channelId !== channelId) {
                return;
            }
            if (subscription?.topic && event?.topic !== subscription.topic) {
                return;
            }
            if (subscription?.entity?.entityId && event?.entityId !== subscription.entity.entityId) {
                return;
            }

            callback(event);
        });
    }

    const markSubscriptionForUnsubscribe = () => {
        // delete subscriptions[subscriptionId];

        // Socket.send({
        //     action: 'unsubscribe',
        //     channelId,
        //     jwt: API.getToken(),
        //     ...sub
        // });
    }

    const subscribe = (subscription, callback) => {
        if (!subscription) {
            return;
        }

        const subscriptionId = hashCode(subscription);

        subscriptions[subscriptionId] = {
            subscription: {...subscription, channelId},
            callback,
        };

        subscriptionQueue.push(subscription);
        setTimeout(subscribeToQueue, subscriptionTimeout);

        // Socket.send({
        //     action: 'subscribe',
        //     channelId,
        //     jwt: API.getToken(),
        //     ...subscription
        // });

        return () => markSubscriptionForUnsubscribe(subscriptionId);
    }

    const subscribeToQueue = () => {
        if (subscriptionQueue.length === 0) {
            return;
        }

        do {
            const subscriptions = subscriptionQueue.splice(0, 50);

            Socket.send({
                action: 'subscribe',
                items: subscriptions,
                context: {
                    jwt: API.getToken(),
                    channelId,
                },
            });

        } while (subscriptionQueue.length > 0);
    }

    const resubscribe = () => {
        Object.values(subscriptions).forEach(sub => {
            // console.log('Resubscribing', sub);
            const {subscription, callback} = sub;
            subscribe(subscription, callback);
        });
    }

    const providerValues = {
        isConnected,
        subscribe,
        getRecipient: (topic) => channelId+'_'+topic,
    };

    return <SocketContext.Provider value={providerValues}>
        {children}
    </SocketContext.Provider>
}

export default Provider;
